import type { Document } from '../api/authoring'

/** The component type that collects a submission. */
export const FORM_TYPE = 'Form'

/**
 * One input on a form, as a submission has to read it back.
 *
 * Narrower than what the type declares, and deliberately: this client
 * reads a form to *label what somebody answered*, so what it needs is a
 * name, a label and whether the field carries a role. Everything else a
 * Form field holds — validation, options, placeholder — is the editor's
 * business and not this screen's.
 */
export type FormField = {
  name: string
  label?: string
  role?: string
}

/**
 * The roles the platform gives a field a meaning by.
 *
 * **Looked up, never guessed at.** The same four the email renderer uses
 * for its subject and its `Reply-To`, so a screen saying "who from" and
 * an email addressed to reply to that person are answering the same
 * question with the same answer. A form declaring none has no answer, and
 * the screen says so rather than picking the longest field.
 */
export const ROLE_NAME = 'name'
export const ROLE_EMAIL = 'email'
export const ROLE_SUBJECT = 'subject'
export const ROLE_MESSAGE = 'message'

/** The fields of a Form document, and undefined for anything else. */
export function formFields(document: Document | undefined): FormField[] | undefined {
  if (!document || document.type !== FORM_TYPE) return undefined
  const fields = document.fields?.fields
  if (!Array.isArray(fields)) return undefined
  return fields.filter(isField)
}

function isField(candidate: unknown): candidate is FormField {
  return typeof candidate === 'object' && candidate !== null && typeof (candidate as FormField).name === 'string'
}

/** The answer to the field carrying a role, and undefined for a form that declares none. */
export function roledAnswer(
  fields: FormField[] | undefined,
  values: Record<string, unknown>,
  role: string,
): string | undefined {
  const field = fields?.find((candidate) => candidate.role === role)
  if (!field) return undefined
  const answer = values[field.name]
  if (typeof answer !== 'string' || answer.trim() === '') return undefined
  return answer
}

/**
 * Who a submission appears to be from: a name when the form asked for
 * one, an address otherwise.
 *
 * Appears to be, and the screen says so. Nothing here is verified — it is
 * what somebody typed into a public form — and a column headed "From"
 * over unverified text is the one place this screen could mislead a
 * reader about what it knows.
 */
export function submitterOf(fields: FormField[] | undefined, values: Record<string, unknown>): string | undefined {
  return roledAnswer(fields, values, ROLE_NAME) ?? roledAnswer(fields, values, ROLE_EMAIL)
}

/** What a submission is about: the subject-roled answer, else the message. */
export function subjectOf(fields: FormField[] | undefined, values: Record<string, unknown>): string | undefined {
  return roledAnswer(fields, values, ROLE_SUBJECT) ?? roledAnswer(fields, values, ROLE_MESSAGE)
}
